import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { User } from '@app/core/interfaces';
import { LogoutType } from '@app/core/enums/logout-types.enum';
import { UserRoles } from '@app/core/enums/userRoles.enum';
import { TokenStorageService } from '@app/core/services/token-storage.service';
import { AppState } from '@app/core/store/app.state';
import { LogoutRequested, StopImpersonation } from '@app/core/store/userAuth/userAuth.actions';
import { selectIsImpersonating, selectUser } from '@app/core/store/userAuth/userAuth.selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dashboard-header',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './dashboard-header.component.html',
  styleUrl: './dashboard-header.component.scss'
})
export class DashboardHeaderComponent implements OnInit, OnDestroy {
  @Input() title!: string;
  @Input() dashboardType!: string;

  ngDestroy$ = new Subject();
  userData!: User;
  isImpersonating = false;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private tokenStorageService: TokenStorageService,
  ) {}

  ngOnInit(): void {
    this.subscribeToUser();
    this.subscribeToImpersonation();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  get userInitial(): string {
    return this.userData?.firstName?.trim().charAt(0).toUpperCase() ?? '';
  }

  get portalTitle(): string {
    switch (this.userData?.role) {
      case UserRoles.adminUser:
        return 'Partner Portal Admin';
      case UserRoles.referralUser:
        return 'Partner Portal Referral';
      case UserRoles.workCompUser:
        return 'Partner Portal Work Comp';
      default:
        return 'Partner Portal';
    }
  }

  stopImpersonating(): void {
    this.store.dispatch(StopImpersonation( {  user: this.tokenStorageService.getEffectiveUser() }));
  }

  subscribeToUser() {
    this.store.select(selectUser)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((user: User | null) => {
        if (user) this.userData = user;
      });
  }

  subscribeToImpersonation() {
    this.store.select(selectIsImpersonating)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((isUserImpersinating) => {this.isImpersonating = isUserImpersinating; });
  }

  navigateToDashboard(): void {
    this.router.navigate([this.dashboardType]);
  }

  navigateToPreferences(): void {
    this.router.navigate([`${this.dashboardType}/preferences`]);
  }

  handleLogout(): void {
    this.store.dispatch(LogoutRequested({ logoutType: LogoutType.USER_INITIATED }));
  }
}
